interface Props {
  /** Total width of the pattern strip. Defaults to fill container. */
  width?: number | string
  /** Height of the strip in pixels. Default 24. */
  height?: number
  color?: string
  opacity?: number
  /** Number of repeating wave units. Default 12 (wide strips look balanced). */
  units?: number
  className?: string
  /** Stroke thickness. Default 1.2. */
  stroke?: number
}

/**
 * Ombak motif — Indonesian batik-derived wave pattern, two-line variant.
 * Designed for: section dividers, hero watermarks, footer accents.
 *
 * Geometric (not realistic): repeating sine-wave-like arcs at two heights,
 * staggered so the eye perceives a "wave field" without it ever feeling busy.
 * Use opacity 0.08-0.18 for watermarks, 0.3-0.5 for divider lines.
 */
export function OmbakPattern({
  width = '100%',
  height = 24,
  color = 'currentColor',
  opacity = 0.18,
  units = 12,
  className,
  stroke = 1.2,
}: Props) {
  // Each wave unit is 24 units wide. Build a path that loops `units` times.
  const unitW = 24
  const totalW = unitW * units

  // Two-row wave path: top wave (h=0..8) and bottom wave (h=10..18), offset
  // so the bottom row's crests align with the top row's troughs.
  let topPath = `M 0 8`
  for (let i = 0; i < units; i++) {
    const x = i * unitW
    topPath += ` Q ${x + 6} 0 ${x + 12} 8 T ${x + 24} 8`
  }

  let botPath = `M 0 18`
  for (let i = 0; i < units; i++) {
    const x = i * unitW
    botPath += ` Q ${x + 6} 10 ${x + 12} 18 T ${x + 24} 18`
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${totalW} 24`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <g stroke={color} strokeWidth={stroke} fill="none" opacity={opacity} strokeLinecap="round">
        <path d={topPath} />
        <path d={botPath} />
      </g>
    </svg>
  )
}
